import React, { useCallback, useState } from 'react';
import SuperadminLayout from '../../components/SuperadminLayout';
import TicketViewModal from '../../components/TicketViewModal';
import { searchTicketsBySerial } from '../../api/superadmin';
import './SuperadminTicketSearchPage.css';

// Recherche Ticket (2026-10-02) : retrouver une fiche par son SERIAL (imprime
// "Ser.:" sur le papier) dans TOUTES les companies, annulees comprises — cf.
// tickets/views.py:AdminTicketSearchView. Clic sur une ligne = detail complet
// (boules, gains, statut). "Aucune fiche" = le serveur ne l'a JAMAIS recue.
const money = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const statusOf = (r) => {
  if (r.is_deleted) return { label: `Annulée${r.deleted_by ? ` (${r.deleted_by})` : ''}`, cls: 'sts-st-deleted' };
  if (r.is_paid) return { label: 'Payée', cls: 'sts-st-paid' };
  if (r.total_gain > 0) return { label: 'Gagnante', cls: 'sts-st-win' };
  return { label: 'Active', cls: 'sts-st-active' };
};

const SuperadminTicketSearchPage = () => {
  const [serial, setSerial] = useState('');
  const [date, setDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);
  const openFetcher = useCallback(() => Promise.resolve(open), [open]);

  const submit = async (e) => {
    e.preventDefault();
    const s = serial.trim();
    if (s.length < 4) { setError('Entre au moins 4 chiffres du serial.'); return; }
    setLoading(true);
    setError('');
    try {
      setData(await searchTicketsBySerial({ serial: s, date }));
    } catch (err) {
      setError('Recherche impossible.');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SuperadminLayout>
      <h2 className="sts-title">Recherche Ticket par serial</h2>
      <p className="sts-help">
        Toutes les companies, fiches annulées comprises. Serial complet (8 chiffres « Ser.: » du
        papier) ou une partie (4 chiffres minimum). Date optionnelle.
      </p>

      <form className="sts-search" onSubmit={submit}>
        <input
          type="text"
          inputMode="numeric"
          placeholder="Serial (ex. 50750502)"
          value={serial}
          onChange={(e) => setSerial(e.target.value)}
          autoFocus
        />
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <button type="submit" disabled={loading}>{loading ? '…' : 'Chercher'}</button>
        {date && <button type="button" onClick={() => setDate('')}>Toutes dates</button>}
      </form>

      {error && <p className="sts-error">{error}</p>}

      {data && (
        data.count === 0 ? (
          <div className="sts-notfound">
            <strong>Aucune fiche avec le serial « {data.serial} » sur le serveur.</strong>
            <br />
            Si le client a bien un ticket imprimé avec ce serial, la fiche n&apos;est jamais arrivée au
            serveur : vendue hors ligne et pas encore synchronisée (ou refusée à la synchro) sur le POS.
          </div>
        ) : (
          <>
            <p className="sts-count">{data.count} fiche(s) — cliquer une ligne pour voir les boules</p>
            <table className="sts-table">
              <thead>
                <tr>
                  <th>Serial</th><th>Company</th><th>Banque</th><th>Loterie</th>
                  <th>N°</th><th>SQ</th><th>Vendue le</th><th>Type</th><th>Total</th><th>Gain</th><th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => {
                  const st = statusOf(r);
                  return (
                    <tr key={r.ticket_id} className="sts-row" onClick={() => setOpen(r)}>
                      <td className="sts-serial">{r.serial}</td>
                      <td>{r.company_code} - {r.company_name}</td>
                      <td>{r.bank_seq ?? '—'} - {r.bank_name}{r.bank_username ? ` (${r.bank_username})` : ''}</td>
                      <td>{r.tirage_code}-{r.tirage_name}</td>
                      <td>{r.no ?? '—'}</td>
                      <td>{r.sq ?? '—'}</td>
                      <td>{r.date} {r.time}</td>
                      <td>
                        {r.online ? 'Online' : `Local (reçue ${r.received_at})`}
                        {r.is_overtime ? ' · overtime' : ''}
                      </td>
                      <td>{money(r.total_amount)}</td>
                      <td>{r.total_gain > 0 ? money(r.total_gain) : '—'}</td>
                      <td><span className={`sts-status ${st.cls}`}>{st.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )
      )}

      {open && (
        <TicketViewModal ticketId={open.ticket_id} fetcher={openFetcher} onClose={() => setOpen(null)} />
      )}
    </SuperadminLayout>
  );
};

export default SuperadminTicketSearchPage;
